import React from 'react'
import styled from 'styled-components'
import {useDebouncedCallback} from 'use-debounce'

import {
  Box as MuiBox,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormLabel,
  Grid,
  TextField,
} from '@material-ui/core'
import {spacing} from '@material-ui/system'
import {makeStyles} from '@material-ui/core/styles'
import {Autocomplete} from '@material-ui/lab'

import * as yup from 'yup'

import {useQueryPaginator} from 'src/components/Table'
import {useForm} from 'src/forms'
import {setApiErrors} from 'src/forms/errors'
import {skuit} from 'src/services/skuit'
import {Facility} from 'src/api/models'
import {AxiosError} from 'axios'
import {handleErrorMessage, Notification} from 'src/components/Notification'
import toast from 'react-hot-toast'

const Box = styled(MuiBox)(spacing)

const useStyles = makeStyles(() => ({
  form: {
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
}))

interface FacilityProps {
  name: string
  address: {
    address1: string
    address2: string
    city: string
    state: string
    postal_code: string //  eslint-disable-line camelcase
  }
  phone: string
}

const facilitySchema = yup.object().shape({
  market: yup.string().label('Market').required(),
  name: yup.string().label('Name').required(),
  address: yup.object({
    address1: yup.string().label('Address 1').required(),
    address2: yup.string().label('Address 2'),
    city: yup.string().label('City'),
    state: yup.string().label('State'),
    postal_code: yup.string().label('Postal Code'),
  }),
  phone: yup.string().label('Phone Number').optional(),
})

interface Props {
  open: boolean
  handleClose: () => void
  onSave?: () => void
  update?: boolean
  facility?: Facility
}

export const FacilityDialog = (props: Props) => {
  const {open, handleClose, onSave, update, facility} = props
  const classes = useStyles()

  const marketPaginator = useQueryPaginator('market-list', skuit.sc.markets.list)
  const dePatchMarketFilters = useDebouncedCallback(marketPaginator.patchFilters, 100)

  const onSubmit = (data: FacilityProps) => {
    let func = skuit.sc.facilities.create
    let action = 'creat'
    if (update) {
      func = (data: FacilityProps) => skuit.sc.facilities.patch(facility?.guid || '', data)
      action = 'updat'
    }
    func(data)
      .then((newFacility: Facility) => {
        handleClose()
        if (onSave) {
          onSave()
        }
        toast.success(
          <Notification
            message={`Successfully ${action}ed Facility: ${newFacility.name}`}
            newObject={newFacility}
          />
        )
      })
      .catch((error: AxiosError) => {
        handleErrorMessage(error, action, 'Facility')
        setApiErrors(formData.setError, error)
      })
  }

  const {Form, Field, data: formData} = useForm(facilitySchema)

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <Form formProps={{className: classes.form}} onSubmit={onSubmit}>
        <DialogTitle>Create Facility</DialogTitle>
        <DialogContent>
          <Field
            name="market"
            defaultValue={facility?.market}
            getSubmitValue={(market) => market.guid}
            renderField={({name, value, setValue, error}) => (
              <Autocomplete
                id={name}
                options={marketPaginator.data.results}
                getOptionLabel={(market) => market?.name || ''}
                getOptionSelected={(option, value) => option.guid === value?.guid}
                filterOptions={(option) => option}
                onChange={(event: any, newValue: any | null) => {
                  setValue(name, newValue)
                }}
                value={value}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name={name}
                    label="Market"
                    error={!!error}
                    helperText={error}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      dePatchMarketFilters.callback({search: event.target.value})
                    }
                  />
                )}
              />
            )}
          />
          <Field
            name="name"
            defaultValue={facility?.name}
            renderField={({name, value, setValue, error}) => (
              <TextField
                id={name}
                name={name}
                label="Name"
                error={!!error}
                helperText={error}
                fullWidth
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setValue(name, e.target.value)
                }
                value={value || ''}
              />
            )}
          />
          <Box mt={6}>
            <Field
              name="address"
              renderField={() => (
                <>
                  <FormLabel>Address</FormLabel>
                  <Grid container spacing={4}>
                    <Grid item md={6} sm={12}>
                      <Field
                        name="address1"
                        defaultValue={facility?.address?.address1}
                        renderField={({name, value, setValue, error}) => (
                          <TextField
                            id={name}
                            name={name}
                            variant="standard"
                            label="Address 1"
                            error={!!error}
                            helperText={error}
                            fullWidth
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                              setValue(name, e.target.value)
                            }
                            value={value || ''}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item md={6} sm={12}>
                      <Field
                        name="address2"
                        defaultValue={facility?.address?.address2}
                        renderField={({name, value, setValue, error}) => (
                          <TextField
                            id={name}
                            name={name}
                            variant="standard"
                            label="Address 2"
                            error={!!error}
                            helperText={error}
                            fullWidth
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                              setValue(name, e.target.value)
                            }
                            value={value || ''}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item md={4} sm={12}>
                      <Field
                        name="city"
                        defaultValue={facility?.address?.city}
                        renderField={({name, value, setValue, error}) => (
                          <TextField
                            id={name}
                            name={name}
                            variant="standard"
                            label="City"
                            error={!!error}
                            helperText={error}
                            fullWidth
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                              setValue(name, e.target.value)
                            }
                            value={value || ''}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item md={4} sm={12}>
                      <Field
                        name="state"
                        defaultValue={facility?.address?.state}
                        renderField={({name, value, setValue, error}) => (
                          <TextField
                            id={name}
                            name={name}
                            variant="standard"
                            label="State"
                            error={!!error}
                            helperText={error}
                            fullWidth
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                              setValue(name, e.target.value)
                            }
                            value={value || ''}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item md={4} sm={12}>
                      <Field
                        name="postal_code"
                        defaultValue={facility?.address?.postalCode}
                        renderField={({name, value, setValue, error}) => (
                          <TextField
                            id={name}
                            name={name}
                            variant="standard"
                            label="Postal Code"
                            error={!!error}
                            helperText={error}
                            fullWidth
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                              setValue(name, e.target.value)
                            }
                            value={value || ''}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </>
              )}
            />
          </Box>
          <Field
            name="phone"
            defaultValue={facility?.phone}
            renderField={({name, value, setValue, error}) => (
              <TextField
                id={name}
                name={name}
                label="Phone Number"
                error={!!error}
                helperText={error}
                fullWidth
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setValue(name, e.target.value)
                }
                value={value || ''}
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button type="submit" color="primary" variant="contained" autoFocus>
            Save
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  )
}
